import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import CourseService from "../Service/MasterCourseService";

/**
 * UserManagementPage component to manage and display user information.
 * @returns {JSX.Element} The UserManagementPage component.
 */
function CourseManagementPage() {
  const location = useLocation();
  const { user } = location.state || {}; // Extract user from location.state
  const [courses, setCourses] = useState([]);
  const [courseMasterMessage, setCourseMasterMessage] = useState("");
  const [isCourseUploading, setIsCourseUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const fileInputRef = useRef(null); // Reference to the file input
  const STATUS_MESSAGES = {
    noFile: "Please select a file before uploading.",
    invalidFileType: "Invalid file type. Please select an .xlsx file.",
    uploadFailed: "Error in uploading file.",
    exportFailed: "Error in exporting file.",
    trackMissing: "Track is missing. Unable to proceed.",
    courseMasterUploadSuccess: "Course Master uploaded successfully.",
    courseMasterTemplateDownloadFailed: "Error in downloading Course Master template.",
    statusPlaceholder: "No recent action.",
    courseMasterUploadInProgress: "Uploading Course Master...",
    supportedFormat: "Supported format: .xlsx",
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [courses]);

  /**
   * Fetches all users from the server.
   */
  const fetchCourses = async () => {
    try {
      const response = await CourseService.getAllCoursesByTrack(user.track);
      setCourses(response);
    } catch (error) {
      console.error("Error fetching Courses:", error);
    }
  };

  /**
   * Deletes a user by ID.
   * @param {string} userId - The ID of the user to delete.
   */
  const deleteCourse = async (courseId) => {
    try {
      const confirmDelete = window.confirm("Are you sure you want to delete this course?");
      if (confirmDelete) {
        await CourseService.deleteCourse(courseId);
        setCourses(courses.filter((course) => course.courseId !== courseId));
      }
    } catch (error) {
      console.error("Error deleting course:", error);
    }
  };

  const refreshCourses = async (track) => {
    if (!track) return;
    const response = await CourseService.getAllCoursesByTrack(track);
    setCourses(response);
  };

  const isExcelFile = (file) => {
    if (!file?.name) return false;
    return file.name.toLowerCase().endsWith(".xlsx");
  };

  const getStatusClassName = (message) => {
    if (!message) return "text-gray-400";
    if (/failed|missing|please select|invalid/i.test(message)) return "text-red-600";
    if (/selected|uploading/i.test(message)) return "text-blue-600";
    return "text-green-600";
  };

  const triggerFileDownload = (response, fallbackFileName) => {
    const contentDisposition = response?.headers?.["content-disposition"] || "";
    const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    const fileName = fileNameMatch?.[1] || fallbackFileName;

    const fileUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(fileUrl);

    return fileName;
  };

  const handleCourseMasterFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!isExcelFile(file)) {
        setSelectedFile(null);
        setCourseMasterMessage(STATUS_MESSAGES.invalidFileType);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setSelectedFile(file);
      setCourseMasterMessage(`File selected: ${file.name}`);
    }
  };

  const handleCourseMasterFileUpload = async () => {
    if (!selectedFile) {
      setCourseMasterMessage(STATUS_MESSAGES.noFile);
      return;
    }

    const activeTrack = user?.track;
    if (!activeTrack) {
      setCourseMasterMessage(STATUS_MESSAGES.trackMissing);
      return;
    }

    const formData = new FormData();
    formData.append("formData", selectedFile);
    setIsCourseUploading(true);
    setCourseMasterMessage(STATUS_MESSAGES.courseMasterUploadInProgress);

    try {
      const response = await CourseService.courseMasterImport(activeTrack, formData);
      if (response != null) {
        setSelectedFile(null);
        setCourseMasterMessage(STATUS_MESSAGES.courseMasterUploadSuccess);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        await refreshCourses(activeTrack);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setCourseMasterMessage(STATUS_MESSAGES.uploadFailed + error.message);
    } finally {
      setIsCourseUploading(false);
    }
  };

  const handleExportCourses = async () => {
    try {
      const activeTrack = user?.track;
      if (!activeTrack) {
        setCourseMasterMessage(STATUS_MESSAGES.trackMissing);
        return;
      }

      const response = await CourseService.exportCourseMasterByTrack(activeTrack);
      const fileName = triggerFileDownload(response, `course-master-${activeTrack}.xlsx`);
      setCourseMasterMessage(`${fileName} downloaded successfully.`);
    } catch (error) {
      console.error("Error exporting course master:", error);
      setCourseMasterMessage(STATUS_MESSAGES.exportFailed + error.message);
    }
  };

  const handleDownloadCourseMasterTemplate = async () => {
    try {
      const response = await CourseService.downloadCourseMasterTemplate();
      const fileName = triggerFileDownload(response, "CourseMasterTemplate.xlsx");
      setCourseMasterMessage(`${fileName} downloaded successfully.`);
    } catch (error) {
      console.error("Error downloading course master template:", error);
      setCourseMasterMessage(STATUS_MESSAGES.courseMasterTemplateDownloadFailed + error.message);
    }
  };

  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(courses.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCourses = courses.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div>
      <h2 className="text-2xl w-1/3 mx-auto font-semibold mt-2 mb-3">Courses Management</h2>
      <div className="w-11/12 max-w-2xl mx-auto bg-gray-100 rounded-lg shadow-lg p-1.5">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <h4 className="inline-flex items-center rounded-full bg-gray-200 px-3 py-1 text-sm font-semibold text-gray-700">
            Course Master
          </h4>
          <span className="text-xs text-gray-500">{STATUS_MESSAGES.supportedFormat}</span>
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            onChange={handleCourseMasterFileChange}
            className="block w-[230px] sm:w-[260px] text-sm text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <button
            type="button"
            onClick={handleCourseMasterFileUpload}
            disabled={isCourseUploading || !selectedFile}
            className="inline-flex w-auto min-w-[120px] justify-center px-3 py-1.5 text-sm font-semibold text-white bg-blue-500 rounded-lg shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isCourseUploading ? "Uploading..." : "Upload File"}
          </button>
        </div>
       
        <div className="mt-1 flex items-center gap-2 text-sm">
          <p className={`min-h-[16px] text-left text-xs font-medium ${getStatusClassName(courseMasterMessage)}`} title={courseMasterMessage || STATUS_MESSAGES.statusPlaceholder}>
            {courseMasterMessage || STATUS_MESSAGES.statusPlaceholder}
          </p>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
              onClick={handleExportCourses}
            >
              Export Data
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
              onClick={handleDownloadCourseMasterTemplate}
            >
              Download Template
            </button>
            <button className="bg-green-500 text-white py-1 px-2 rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400">
              <Link to={{
                pathname: "/admin/add-course",
                state: { user }
              }}>Add Course</Link>
            </button>
          </div>
        </div>
      </div>
       <br/>
        <div className="h-2" />
        <table className="w-11/12 mx-auto bg-white border border-gray-300">
          <thead>
            <tr>
              <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                Group Course Code
              </th>
              <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                Group Course Name
              </th>
              <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                Component Course Code
              </th>
              <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                Component Course Name
              </th>
              <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                Mode Of Learning
              </th>
              <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                Duration In Hours
              </th>
              <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                Duration In Days
              </th>
              <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                No of SME's
              </th>
              <th className="text-sm py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300">
                Track
              </th>
              <th className="py-1 px-1 bg-gray-200 text-left text-gray-600 font-bold border border-gray-300" colSpan="2">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedCourses.map((course) => (
              <tr key={course.courseId}>
                <td className="py-1 px-1 border border-gray-300 text-sm">{course.groupCourseId}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{course.groupCourseName}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{course.componentCourseId}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{course.componentCourseName}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{course.modeOfLearning}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{course.durationInHours}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{course.durationInDays}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{course.numberOfSMEsRequired}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{course.track}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">
                  <button className="bg-green-500 text-white py-1 px-1 rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400" onClick={() => deleteCourse(course.courseId)}>
                    Delete
                  </button>
                </td>
                <td className="py-1 px-1 border border-gray-300 text-sm">
                  <button className="bg-green-500 text-white py-1 px-1 rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400">
                    <Link
                      to={{
                        pathname: `/admin/update-course/${course.courseId}`,
                        state: { user }
                      }}
                      className="text-purple-700 hover:underline"
                    >
                      Update
                    </Link>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-2 flex items-center justify-center gap-3 text-sm">
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <br />

    </div>

      );
}

      export default CourseManagementPage;
